import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import '../../../App.css';
import { db, products_col_name, categories_col_name, promotions_col_name } from '../../../firebase';
import { doc, getDoc, setDoc, serverTimestamp, getDocs, collection } from 'firebase/firestore';

/** Generate custom product ID: DTA + slug(productName) + unique suffix */
function generateProductId(productName) {
    const slug = (productName || 'product')
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
        .slice(0, 50) || 'product';
    const unique = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    return `DTA_${slug}_${unique}`;
}

const emptyForm = {
    name: '',
    price: '',
    size: '',
    color: '',
    category: '',
    ageRate: '',
    description: '',
    coverImage: '',
    extraImagesCount: 0,
    extraImages: [],
    onPromotion: false,
    promotionPrice: '',
};

export const ManageProducts = () => {
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [loadDocLoading, setLoadDocLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [categories, setCategories] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [editingProductId, setEditingProductId] = useState('');
    const [productIdInput, setProductIdInput] = useState('');
    const [formData, setFormData] = useState({ ...emptyForm });

    /** Load product by ID (from input or from location state) and fill form */
    const loadProductById = useCallback(async (id) => {
        if (!id || !id.trim()) return;
        const docId = id.trim();
        setLoadDocLoading(true);
        setError(null);
        try {
            const ref = doc(db, products_col_name, docId);
            const snap = await getDoc(ref);
            if (snap.exists()) {
                const data = snap.data();
                const extra = data.extraImages || [];
                setFormData({
                    name: data.name ?? '',
                    price: data.price != null ? String(data.price) : '',
                    size: data.size ?? '',
                    color: data.color ?? '',
                    category: data.category ?? '',
                    ageRate: data.ageRate ?? '',
                    description: data.description ?? '',
                    coverImage: data.coverImage ?? '',
                    extraImagesCount: extra.length,
                    extraImages: [...extra],
                    onPromotion: Boolean(data.onPromotion),
                    promotionPrice: data.promotionPrice != null ? String(data.promotionPrice) : '',
                });
                setEditingProductId(docId);
                setProductIdInput(docId);
                setSuccess(true);
                setTimeout(() => setSuccess(false), 3000);
            } else {
                setError('Product not found. Use this ID to create a new product, or enter another ID to load.');
                setEditingProductId('');
                setFormData({ ...emptyForm });
            }
        } catch (err) {
            console.error('Error loading product:', err);
            setError('Failed to load product.');
        } finally {
            setLoadDocLoading(false);
        }
    }, []);

    useEffect(() => {
        const editId = location.state?.editProductId;
        if (editId && typeof editId === 'string') {
            loadProductById(editId);
        }
    }, [location.state?.editProductId, loadProductById]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const snapshot = await getDocs(collection(db, categories_col_name));
                const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
                setCategories(list);
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        const fetchPromotions = async () => {
            try {
                const snapshot = await getDocs(collection(db, promotions_col_name));
                const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
                setPromotions(list);
            } catch (err) {
                console.error('Error fetching promotions:', err);
            }
        };
        fetchPromotions();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'price' || name === 'promotionPrice') {
            const digitsOnly = value.replace(/\D/g, '');
            setFormData(prevState => ({
                ...prevState,
                [name]: digitsOnly
            }));
            return;
        }

        if (name === 'extraImagesCount') {
            const count = Math.min(20, Math.max(0, parseInt(value, 10) || 0));
            setFormData(prevState => {
                const currentExtra = prevState.extraImages || [];
                const newExtra = Array.from({ length: count }, (_, i) => currentExtra[i] ?? '');
                return { ...prevState, extraImagesCount: count, extraImages: newExtra };
            });
            return;
        }
        if (name?.startsWith('extraImage_')) {
            const index = parseInt(name.replace('extraImage_', ''), 10);
            setFormData(prevState => {
                const next = [...(prevState.extraImages || [])];
                next[index] = value;
                return { ...prevState, extraImages: next };
            });
            return;
        }
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        if (formData.onPromotion) {
            const promoPriceNum = Number(formData.promotionPrice) || 0;
            const priceNum = Number(formData.price) || 0;
            if (promoPriceNum >= priceNum) {
                setError("Promotion price must be strictly less than the regular price.");
                setLoading(false);
                return;
            }
        }

        try {
            const productData = {
                name: formData.name,
                price: Number(formData.price) || 0,
                size: formData.size,
                color: formData.color,
                category: formData.category,
                ageRate: formData.ageRate,
                description: formData.description,
                ...(formData.coverImage && { coverImage: formData.coverImage.trim() }),
                ...(formData.extraImages?.length > 0 && {
                    extraImages: formData.extraImages.map((url) => url?.trim()).filter(Boolean)
                }),
                onPromotion: formData.onPromotion,
                ...(formData.onPromotion && formData.promotionPrice !== '' && {
                    promotionPrice: Number(formData.promotionPrice),
                    onPromotionStartDate: serverTimestamp(),
                }),
                updatedAt: serverTimestamp()
            };

            const docId = editingProductId || generateProductId(formData.name);
            const ref = doc(db, products_col_name, docId);
            const existing = await getDoc(ref);

            if (existing.exists()) {
                await setDoc(ref, productData, { merge: true });
                setSuccess(true);
                setEditingProductId(docId);
                setProductIdInput(docId);
            } else {
                await setDoc(ref, {
                    ...productData,
                    createdAt: serverTimestamp()
                });
                setSuccess(true);
                setEditingProductId(docId);
                setProductIdInput(docId);
            }

            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            console.error("Error saving product: ", err);
            setError("Failed to save product. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleClearForm = () => {
        setFormData({ ...emptyForm });
        setEditingProductId('');
        setProductIdInput('');
        setError(null);
    };

    // Preview payload (no timestamps)
    const previewProduct = {
        name: formData.name || '—',
        price: formData.price ? `${formData.price} TZS` : '—',
        size: formData.size || '—',
        color: formData.color || '—',
        category: formData.category || '—',
        ageRate: formData.ageRate || '—',
        description: formData.description || 'No description.',
        coverImage: formData.coverImage?.trim() || null,
        extraImages: (formData.extraImages || []).map((u) => u?.trim()).filter(Boolean),
        onPromotion: formData.onPromotion,
        promotionPrice: formData.promotionPrice,
        promotionTitle: formData.promotionTitle
    };
    const displayPrice = previewProduct.onPromotion && previewProduct.promotionPrice
        ? `${previewProduct.promotionPrice} TZS`
        : previewProduct.price;

    return (
        <section className="admin-section add-products-section fade-in">
            <h2>{editingProductId ? 'Edit Product' : 'Add New Product'}</h2>
            <div className="add-products-layout">
            <form className="admin-form add-products-form" onSubmit={handleSubmit}>
                {/* <div className="form-group form-group--full" style={{ marginBottom: '16px' }}>
                    <label>Product ID (DTA + name + unique)</label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                            type="text"
                            value={productIdInput}
                            onChange={(e) => setProductIdInput(e.target.value)}
                            placeholder="e.g. DTA_abaya_1234567890_abc12 or leave empty for new"
                            style={{ flex: '1', minWidth: '200px' }}
                        />
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => loadProductById(productIdInput)}
                            disabled={loadDocLoading || !productIdInput.trim()}
                        >
                            {loadDocLoading ? 'Loading…' : 'Load product'}
                        </button>
                        {editingProductId && (
                            <button type="button" className="btn-primary" onClick={handleClearForm} style={{ background: '#666' }}>
                                New product
                            </button>
                        )}
                    </div>
                    {editingProductId && (
                        <small style={{ display: 'block', marginTop: '6px', color: '#666' }}>
                            Editing: <strong>{editingProductId}</strong> — submit to update.
                        </small>
                    )}
                </div> */}
                <div className="add-products-form-grid">
                    <div className="form-group">
                        <label>Product Name <span className="required">*</span></label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Product Name"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Size</label>
                        <input
                            type="text"
                            name="size"
                            value={formData.size}
                            onChange={handleChange}
                            placeholder="e.g. 45,60,55"
                            maxLength={20}
                        />
                    </div>
                    <div className="form-group">
                        <label>Price</label>
                        <input
                            type="text"
                            name="price"
                            value={formData.price}
                            // value={formData.price.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            onChange={handleChange}
                            placeholder="e.g. 60,000"
                            maxLength={20}
                            pattern='[0-9]+'
                            
                        />
                    </div>
                    <div className="form-group">
                        <label>Color</label>
                        <input
                            type="text"
                            name="color"
                            value={formData.color}
                            onChange={handleChange}
                            placeholder="black, red, blue..."
                            maxLength={50}
                        />
                    </div>
                    <div className="form-group">
                        <label>Category <span className="required">*</span></label>
                        <select
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select Category</option>
                            {categories.map((cat) => (
                                <option key={cat.id} value={cat.name}>
                                    {cat.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* <div className="form-group">
                        <label>Age rate <span className="required">*</span></label>
                        <select
                            name="ageRate"
                            value={formData.ageRate}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select Age rate</option>
                            <option value="Child">Child</option>
                            <option value="Adult">Adult</option>
                            <option value="Senior">Senior</option>
                        </select>
                    </div> */}
                </div>

                <div className="form-group form-group--full">
                    <label>Description</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Product description..."
                    />
                </div>

                <div className="form-group form-group--full">
                    <label>Cover image URL</label>
                    <input
                        type="url"
                        name="coverImage"
                        value={formData.coverImage}
                        onChange={handleChange}
                        placeholder="https://example.com/cover.jpg"
                    />
                </div>

                <div className="form-group">
                    <label>Number of extra images (0–20)</label>
                    <input
                        type="number"
                        name="extraImagesCount"
                        value={formData.extraImagesCount || ''}
                        onChange={handleChange}
                        placeholder="0"
                        min={0}
                        max={20}
                    />
                </div>

                {(formData.extraImagesCount > 0) && (
                    <div className="form-group form-group--full extra-images-group">
                        <label>Extra image URLs</label>
                        {Array.from({ length: formData.extraImagesCount }, (_, i) => (
                            <input
                                key={i}
                                type="url"
                                name={`extraImage_${i}`}
                                value={formData.extraImages?.[i] ?? ''}
                                onChange={handleChange}
                                placeholder={`Extra image ${i + 1} URL`}
                                style={{ marginBottom: '8px' }}
                            />
                        ))}
                    </div>
                )}

                <div className="form-group form-group--promotion-toggle">
                    <label className="promotion-toggle-label">
                        <input
                            type="checkbox"
                            name="onPromotion"
                            checked={formData.onPromotion}
                            onChange={handleChange}
                        />
                        <span className="promotion-toggle-text">On promotion</span>
                    </label>
                </div>

                {formData.onPromotion && (
                    <div className="promotion-fields">
                        <div className="add-products-form-grid add-products-form-grid--promotion">
                            <div className="form-group">
                                <label>Promotion price</label>
                                <input
                                    type="text"
                                    name="promotionPrice"
                                    value={formData.promotionPrice}
                                    onChange={handleChange}
                                    placeholder="e.g. 60000"
                                    maxLength={20}
                                    pattern="[0-9]+"
                                />
                            </div>
                            {/* <div className="form-group">
                                <label>Promotion title <span className="required">*</span></label>
                                <select
                                    name="promotionTitle"
                                    value={formData.promotionTitle}
                                    onChange={handleChange} 
                                    required
                                >
                                    <option value="">Select Promotion title</option>
                                    {promotions.map((promotion) => (
                                        <option key={promotion.id} value={promotion.name}>
                                            {promotion.name}
                                        </option>
                                    ))}
                                </select>
                            </div> */}
                        </div>
                    </div>
                )}

                <div className="form-actions">
                    <button type="submit" className="btn-primary" disabled={loading}>
                        {loading ? 'Saving…' : editingProductId ? 'Update product' : 'Add product'}
                    </button>
                </div>
            </form>

            <aside className="add-products-preview">
                <h3 className="add-products-preview__title">Preview</h3>
                <div className="add-products-preview__card">
                    <div className="add-products-preview__image-wrap">
                        <img
                            src={previewProduct.coverImage || 'https://placehold.co/300x400?text=Cover+image'}
                            alt={previewProduct.name}
                            onError={(e) => { e.target.src = 'https://placehold.co/300x400?text=Invalid+URL'; }}
                        />
                        {previewProduct.onPromotion && (
                            <span className="add-products-preview__badge">Promotion</span>
                        )}
                    </div>
                    <div className="add-products-preview__body">
                        <h4 className="add-products-preview__name">{previewProduct.name}</h4>
                        <p className="add-products-preview__description">
                            {previewProduct.description.length > 120
                                ? `${previewProduct.description.slice(0, 120)}...`
                                : previewProduct.description}
                        </p>
                        <div className="add-products-preview__meta">
                            <span>Price: {displayPrice}</span>
                            {previewProduct.size !== '—' && <span>Size: {previewProduct.size}</span>}
                            {previewProduct.color !== '—' && <span>Color: {previewProduct.color}</span>}
                            <span>Category: {previewProduct.category}</span>
                            <span>Age: {previewProduct.ageRate}</span>
                        </div>
                        {previewProduct.extraImages.length > 0 && (
                            <div className="add-products-preview__gallery">
                                <span className="add-products-preview__gallery-label">Extra images:</span>
                                <div className="add-products-preview__thumbs">
                                    {previewProduct.extraImages.map((url, i) => (
                                        <img
                                            key={i}
                                            src={url}
                                            alt={`Extra ${i + 1}`}
                                            onError={(e) => { e.target.src = 'https://placehold.co/60x60?text=Error'; }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </aside>
            </div>

            {success && <div className="alert alert-success" style={{
                padding: '10px',
                backgroundColor: '#d4edda',
                color: '#155724',
                marginBottom: '15px',
                borderRadius: '4px'
            }}>Product added successfully!</div>}

            {error && <div className="alert alert-danger" style={{
                padding: '10px',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                marginBottom: '15px',
                borderRadius: '4px'
            }}>{error}</div>}
        </section>
    );
};